import React, { useMemo, useState } from "react";
import { Typography, Button, Tag, Table, Space, Form, notification, Tooltip } from "antd";
import {
  HomeOutlined,
  BookOutlined,
  FolderOutlined,
  RightOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { isCourseMember, getCourseProfessors } from "../data";
import * as casesApi from "../api/cases";
import CaseTable from "./CaseTable";
import CaseFormModal from "./CaseFormModal";
import PersonChip from "./PersonChip";

const { Title, Text } = Typography;

const COURSES_PREVIEW = 6;
const CASES_PREVIEW = 10;

const Accueil = ({
  professorName,
  courseList = [],
  allCases = [],
  setAllCases,
  favoriteCourseIds = [],
}) => {
  const { userRole, user } = useAppContext();
  const isProfessor = userRole === "Professeur";
  const navigate = useNavigate();

  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [form] = Form.useForm();

  const editableCourseOptions = useMemo(
    () =>
      courseList
        .filter((c) => isCourseMember(c, user))
        .map((c) => ({ label: c.name, value: c.id })),
    [courseList, professorName]
  );

  const canAddCase = isProfessor && editableCourseOptions.length > 0;

  const handleOpenAddCase = () => {
    form.resetFields();
    setCaseModalOpen(true);
  };

  const handleSubmitCase = async (values) => {
    try {
      const { case: created, jobIds } = await casesApi.createCaseWithImages(values);
      setAllCases?.((prev) => [...prev, created]);
      setCaseModalOpen(false);
      if (jobIds.length) {
        notification.info({ message: "Cas ajouté — traitement des images en cours…" });
        casesApi.pollJobs(jobIds).then(async ({ failed }) => {
          const fresh = await casesApi.getCase(created.key);
          setAllCases?.((prev) => prev.map((c) => (c.key === created.key ? fresh : c)));
          if (failed.length) {
            notification.warning({ message: "Certaines images n'ont pas pu être traitées" });
          } else {
            notification.success({ message: "Images prêtes" });
          }
        });
      } else {
        notification.success({ message: "Cas ajouté" });
      }
    } catch (e) {
      notification.error({ message: "Échec de l'ajout", description: e.message });
    }
  };

  const userName = isProfessor ? professorName : "Julie Dupuis";
  const coursesSectionLabel = isProfessor ? "Mes cours" : "Mes cours favoris";
  const casesSectionLabel = isProfessor ? "Derniers cas" : "Cas de mes cours favoris";
  const coursesViewAllFilter = isProfessor ? "mine" : "favoris";

  const myCourses = useMemo(() => {
    if (isProfessor) {
      return courseList.filter((c) => isCourseMember(c, user));
    }
    return courseList.filter((c) => favoriteCourseIds.includes(c.id));
  }, [courseList, isProfessor, professorName, favoriteCourseIds]);

  const latestCases = useMemo(() => {
    if (myCourses.length === 0) return [];
    const ids = myCourses.map((c) => c.id);
    return allCases
      .filter((c) => ids.includes(c.course))
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, CASES_PREVIEW);
  }, [myCourses, allCases]);

  const courseLabelById = useMemo(() => {
    const m = {};
    courseList.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [courseList]);

  const getCaseCount = (courseId) =>
    allCases.filter((c) => c.course === courseId).length;

  const courseColumns = [
    {
      title: "Cours",
      dataIndex: "name",
      key: "name",
      render: (name, course) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {course.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {course.description}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Professeurs",
      key: "professors",
      render: (_, course) => (
        <Space wrap size={[8, 8]}>
          {getCourseProfessors(course).map((p) => (
            <PersonChip key={p} name={p} />
          ))}
        </Space>
      ),
    },
    {
      title: "Cas",
      key: "cases",
      align: "center",
      width: 80,
      render: (_, course) => getCaseCount(course.id),
    },
    ...(isProfessor
      ? [
          {
            title: "Statut",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (status) =>
              status === "draft" ? (
                <Tag color="orange" icon={<FileTextOutlined />}>
                  Brouillon
                </Tag>
              ) : (
                <Tag color="green">Publié</Tag>
              ),
          },
        ]
      : []),
  ];

  const caseColumns = [
    {
      title: "Pathologie",
      dataIndex: "pathology",
      key: "pathology",
    },
    {
      title: "Région",
      dataIndex: "region",
      key: "region",
    },
    {
      title: "Cours",
      dataIndex: "course",
      key: "course",
      render: (courseId) => {
        const label = courseLabelById[courseId] || courseId;
        if (!courseLabelById[courseId]) {
          return <Text type="secondary">{label}</Text>;
        }
        return (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/cours/${courseId}`);
            }}
          >
            {label}
          </Button>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Propriétaire",
      dataIndex: "owner",
      key: "owner",
      render: (owner) => <PersonChip name={owner} />,
    },
  ];

  const noCoursesEmpty = isProfessor
    ? {
        title: "Vous ne gérez aucun cours",
        description:
          "Créez votre premier cours, ou demandez à être ajouté comme co-éditeur.",
        cta: { label: "Voir tous les cours", action: () => navigate("/cours") },
      }
    : {
        title: "Aucun cours favori",
        description:
          "Ajoutez un cours à vos favoris en cliquant sur l'étoile dans la liste \"Tous\".",
        cta: { label: "Explorer les cours", action: () => navigate("/cours") },
      };

  const sectionHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    gap: 16,
    flexWrap: "wrap",
  };

  return (
    <div
      style={{
        padding: 20,
        background: "var(--color-bg)",
        color: "var(--color-text)",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <Title level={2} style={{ margin: 0, color: "var(--color-text)" }}>
          <HomeOutlined style={{ marginRight: 8 }} />
          Bienvenue, {userName}
        </Title>
      </div>

      <div style={{ marginBottom: 32 }}>
        <div style={sectionHeaderStyle}>
          <Title
            level={4}
            style={{ margin: 0, color: "var(--color-text)" }}
          >
            <BookOutlined style={{ marginRight: 8 }} />
            {coursesSectionLabel}
          </Title>
          {myCourses.length > 0 && (
            <Button
              type="link"
              onClick={() =>
                navigate(`/cours?filter=${coursesViewAllFilter}`)
              }
            >
              Voir tous <RightOutlined />
            </Button>
          )}
        </div>

        <Table
          columns={courseColumns}
          dataSource={myCourses.slice(0, COURSES_PREVIEW)}
          rowKey="id"
          pagination={false}
          onRow={(course) => ({
            onClick: () => navigate(`/cours/${course.id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <div style={{ padding: "32px 16px" }}>
                <Title level={5} style={{ color: "var(--color-text)" }}>
                  {noCoursesEmpty.title}
                </Title>
                <Text type="secondary">{noCoursesEmpty.description}</Text>
                <div style={{ marginTop: 16 }}>
                  <Button type="primary" onClick={noCoursesEmpty.cta.action}>
                    {noCoursesEmpty.cta.label}
                  </Button>
                </div>
              </div>
            ),
          }}
        />
      </div>

      <div>
        <div style={sectionHeaderStyle}>
          <Title
            level={4}
            style={{ margin: 0, color: "var(--color-text)" }}
          >
            <FolderOutlined style={{ marginRight: 8 }} />
            {casesSectionLabel}
          </Title>
          <Space>
            {isProfessor && (
              <Tooltip
                title={
                  canAddCase
                    ? ""
                    : "Vous devez gérer au moins un cours pour ajouter un cas."
                }
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleOpenAddCase}
                  disabled={!canAddCase}
                >
                  Ajouter un cas
                </Button>
              </Tooltip>
            )}
            {latestCases.length > 0 && (
              <Button type="link" onClick={() => navigate("/cas")}>
                Voir tous <RightOutlined />
              </Button>
            )}
          </Space>
        </div>

        {latestCases.length > 0 ? (
          <CaseTable
            dataSource={latestCases}
            columns={caseColumns}
            pagination={false}
          />
        ) : (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              background: "var(--color-bg-container, #fff)",
              border: "1px solid var(--color-border, #f0f0f0)",
              borderRadius: 8,
            }}
          >
            <Title level={5} style={{ color: "var(--color-text)" }}>
              Aucun cas récent
            </Title>
            <Text type="secondary">
              {myCourses.length === 0
                ? "Les cas de vos cours apparaîtront ici."
                : "Aucun cas n'a encore été ajouté à vos cours."}
            </Text>
          </div>
        )}
      </div>

      {isProfessor && (
        <CaseFormModal
          open={caseModalOpen}
          onCancel={() => setCaseModalOpen(false)}
          onFinish={handleSubmitCase}
          form={form}
          isEditing={false}
          courseOptions={editableCourseOptions}
        />
      )}
    </div>
  );
};

export default React.memo(Accueil);
